import React, { useEffect } from 'react';
import { useReportBuilderContext } from '../../report-builder.context';
import { textPanelStyles } from './text-panel.styles';
import { CARD_TYPES } from '../../types';
import { textEditorFontSizeOptions } from '../text-card/text-card';
import { FormattedMessage, useIntl } from 'react-intl';

export const TextPanel = () => {
  const intl = useIntl();
  const { currentCard } = useReportBuilderContext();
  const classes = textPanelStyles();

  useEffect(() => {
    const applyAriaLabels = () => {
      const toolbarElement = document.querySelector(`#toolbar-${currentCard?.i}`);

      if (toolbarElement) {
        const pickerLabels = toolbarElement.querySelectorAll('.ql-picker-label');
        pickerLabels.forEach((picker: Element, index: number) => {
          const ariaLabel = getAccessibleNameForPicker(picker, index);
          picker.setAttribute('aria-label', ariaLabel);
        });

        const pickerItems = toolbarElement.querySelectorAll('.ql-picker-item');
        pickerItems.forEach((item: Element) => {
          const dataValue = item.getAttribute('data-value');
          const ariaLabel = dataValue ? `Font: ${dataValue}` : 'Picker item';
          item.setAttribute('aria-label', ariaLabel);
        });

        const toolbarButtons = toolbarElement.querySelectorAll('button');
        toolbarButtons.forEach(button => {
          if (!button.hasAttribute('aria-label')) {
            const value = button.getAttribute('value');
            button.setAttribute('aria-label', value ? `Align ${value}` : 'Button');
          }
        });
      }
    };

    if (currentCard?.type === CARD_TYPES.TEXT) {
      applyAriaLabels();

      // Reapply labels after potential Quill updates (e.g., dropdown interactions)
      const observer = new MutationObserver(() => applyAriaLabels());
      const toolbarElement = document.querySelector(`#toolbar-${currentCard?.i}`);
      if (toolbarElement) {
        observer.observe(toolbarElement, { childList: true, subtree: true });
      }

      // Cleanup observer on unmount
      return () => observer.disconnect();
    }
  }, [currentCard?.i, currentCard?.type]);

  const getAccessibleNameForPicker = (picker: Element, index: number) => {
    if (picker.closest('.ql-font')) {
      return 'Font Picker';
    } else if (picker.closest('.ql-header')) {
      return 'Font Size Picker';
    } else if (picker.closest('.ql-color')) {
      return 'Text Color Picker';
    } else if (picker.closest('.ql-background')) {
      return 'Background Color Picker';
    } else if (picker.getAttribute('aria-controls')?.includes('ql-picker-options')) {
      const dataValue = picker.getAttribute('data-value');
      if (dataValue) {
        return `Picker for value: ${dataValue}`;
      }
    }
    return `Picker ${index + 1}`;
  };

  return currentCard?.type === CARD_TYPES.TEXT ? (
    <div className={classes.root}>
      <div id={`toolbar-${currentCard?.i}`} className="toolbar">
        <div className="row">
          <div className="half">
            <label htmlFor="ql-font-control">
              <FormattedMessage id="common.font" defaultMessage="Font" />
            </label>
            <select id="ql-font-control" className="ql-font" onChange={e => e.persist()} defaultValue="SageUI">
              <option value="Arial" />
              <option value="times-new-roman" />
              <option value="SageUI" />
              <option value="serif" />
              <option value="monospace" />
            </select>
          </div>
          <div className="half">
            <label htmlFor="ql-text-style-control">
              <FormattedMessage id="common.text_style" defaultMessage="Text Style" />
            </label>
            <select id="ql-text-style-control" className="ql-header" defaultValue={''} onChange={e => e.persist()}>
              <option />
              <option value="1" />
              <option value="2" />
              <option value="3" />
              <option value="4" />
              <option value="5" />
              <option value="6" />
            </select>
          </div>
        </div>
        <label>
          <FormattedMessage id="common.controls" defaultMessage="Controls" />
        </label>
        <div className="row">
          <div className="big">
            <select className="ql-size" defaultValue={'11px'} onChange={e => e.persist()}>
              {textEditorFontSizeOptions.map(e => (
                <option key={e} value={e} />
              ))}
            </select>
          </div>
          <button
            className="ql-link"
            aria-label={intl.formatMessage({ id: 'common.link.aria_label', defaultMessage: 'Link' })}
          />
        </div>
        <div className="row">
          <button
            className="ql-bold"
            aria-label={intl.formatMessage({ id: 'common.bold.aria_label', defaultMessage: 'Bold' })}
          />
          <button
            className="ql-italic"
            aria-label={intl.formatMessage({ id: 'common.italic.aria_label', defaultMessage: 'Italic' })}
          />
          <button
            className="ql-underline"
            aria-label={intl.formatMessage({ id: 'common.underline.aria_label', defaultMessage: 'Underline' })}
          />
          <select className="ql-color" />
          <select className="ql-background" />
        </div>

        <div className="row">
          <button
            className="ql-align"
            value=""
            aria-label={intl.formatMessage({ id: 'common.align.left.aria_label', defaultMessage: 'Left' })}
          />
          <button
            className="ql-align"
            value="center"
            aria-label={intl.formatMessage({ id: 'common.align.center.aria_label', defaultMessage: 'Center' })}
          />
          <button
            className="ql-align"
            value="right"
            aria-label={intl.formatMessage({ id: 'common.align.right.aria_label', defaultMessage: 'Right' })}
          />
          <button
            className="ql-list"
            value="bullet"
            aria-label={intl.formatMessage({ id: 'common.list.bullet.aria_label', defaultMessage: 'Bullet' })}
          />
          <button
            className="ql-list"
            value="ordered"
            aria-label={intl.formatMessage({ id: 'common.list.ordered.aria_label', defaultMessage: 'Ordered' })}
          />
        </div>
      </div>
    </div>
  ) : null;
};
